import debug from 'debug'

import {
  hasConfiguration,
  getConfiguration,
  setConfiguration,

  getRegionConfiguration,

  setBasePrice,
  setOptionsPrice,

  truthy,
  dedupe
} from '~/client/assets/js/common/render-data'

import getPriceColumn from '~/client/assets/js/renderapp/render-data/price-column'

import {
  isSelected,
  isEnabled,
  isChanged,
  isDisabled,

  isDisabledForConfiguration,

  isGroupEnvironments,
  isGroupCameras,

  getButtonObjectPrice,

  hasButtonObjectBasePrice,
  getButtonObjectBasePrice,

  hasButtonObjectFrontEndRule,
  getButtonObjectFrontEndRule,

  getButtonObjectGroup,

  hasButtonObjectUIElement,
  getButtonObjectUIElement,

  getIsConfigured
} from '~/client/assets/js/renderapp/render-data/button-object'

import {
  getAllButtonObjects,
  getAllButtonObjectsForGroup,

  getConfigurationFromButtonObjects,

  getGroupsFromButtonObjects,
  getGroupsFrom,

  hasAnyButtonObjectsEnabledForGroup,

  getButtonObjectsChangedForGroup,
  getButtonObjectsEnabledForGroup
} from '~/client/assets/js/renderapp/render-data/button-objects'

import {
  updateUIElementSelected,
  updateUIElementDisabled
} from '~/client/assets/js/renderapp/render-data/ui-element/change'

import {
  hasDefaultConfigurationFor,
  getDefaultConfigurationFor
} from '~/client/assets/js/renderapp/default-configuration'

const log = debug('renderapp:render-data/button-objects/update')

log('`renderapp` is awake')

export function getToButtonObject (buttonObjects = getAllButtonObjects()) {
  /**
   *  log('getToButtonObject')
   */

  return function toButtonObject (rule) {
    /**
     *  log('toButtonObject')
     */

    const frontEndRule = '+'.concat(rule)

    return (
      buttonObjects
        .filter(hasButtonObjectFrontEndRule)
        .find((buttonObject) => (
          frontEndRule === getButtonObjectFrontEndRule(buttonObject)
        ))
    )
  }
}

export function resetAllButtonObjectsChanged () {
  /**
   *  log('resetAllButtonObjectsChanged')
   */

  getAllButtonObjects()
    .forEach((buttonObject) => {
      delete buttonObject.Changed
    })
}

export function resetButtonObjectsChangedForGroup (group) {
  /**
   *  log('resetButtonObjectsChangedForGroup')
   */

  getButtonObjectsChangedForGroup(group)
    .forEach((buttonObject) => {
      delete buttonObject.Changed
    })
}

export function updateAllButtonObjectsChanged () {
  /**
   *  log('updateAllButtonObjectsChanged')
   */

  if (hasConfiguration()) {
    const currentConfiguration = getConfiguration()

    if (hasDefaultConfigurationFor(currentConfiguration)) {
      const defaultConfiguration = getDefaultConfigurationFor(currentConfiguration)

      const CURRENT_CONFIGURATION = currentConfiguration.split('+').filter(truthy).reduce(dedupe, [])
      const DEFAULT_CONFIGURATION = defaultConfiguration.split('+').filter(truthy).reduce(dedupe, [])

      const toButtonObject = getToButtonObject()

      const CURRENT = CURRENT_CONFIGURATION
        .map(toButtonObject)

      const DEFAULT = DEFAULT_CONFIGURATION
        .map(toButtonObject)

      const CHANGED = CURRENT
        .filter((buttonObject) => !DEFAULT.includes(buttonObject))
        .filter((buttonObject) => !isGroupEnvironments(buttonObject))
        .filter((buttonObject) => !isGroupCameras(buttonObject))

      CHANGED
        .forEach((buttonObject) => {
          buttonObject.Changed = true
        })
    }
  }
}

export function updateAllButtonObjectsSelected () {
  /**
   *  log('updateAllButtonObjectsSelected')
   */

  getGroupsFromButtonObjects()
    .forEach(updateButtonObjectsSelectedForGroup)
}

export function updateButtonObjectsSelectedForGroup (group) {
  /**
   *  log('updateButtonObjectsSelectedForGroup')
   */

  if (hasAnyButtonObjectsEnabledForGroup(group)) {
    /**
     *  Get only enabled for this group!
     */
    const buttonObjects = getButtonObjectsEnabledForGroup(group)

    /**
     *  Is at least one <input /> selected?
     */
    if (buttonObjects.some(isSelected)) {
      /**
       *  Yes
       */
      const buttonObject = buttonObjects.find(isSelected)

      /**
       *  Was this a user choice?
       */
      if (isChanged(buttonObject)) return /* Yes. Exit */
    }

    let buttonObject

    const isConfigured = getIsConfigured(getConfiguration())

    /**
     *  Is at least one `buttonObject` in this group a match by `FrontEndRule` in `Configuration`?
     */
    if (buttonObjects.some(isConfigured)) {
      /**
       *  Yes
       */
      buttonObject = buttonObjects.find(isConfigured)
    } else {
      /**
       *  ... Or use the first `buttonObject` in this collection (since everything in this collection is enabled, the first will do)
       */
      ([
        buttonObject
      ] = buttonObjects)
    }

    if (buttonObject) updateUIElementSelected(buttonObject)
  }
}

export function updateAllButtonObjectsDisabled () {
  /**
   *  log('updateAllButtonObjectsDisabled')
   */

  /**
   *  Compute `buttonObject` visibility (and apply it) then return an array of `ButtonObjects` whose visibility has changed
   */
  const buttonObjects = getAllButtonObjects()
    /**
     *  Update `disabled` state according to visibility rules
     */
    .reduce(updateButtonObjectsDisabled, [])

  getGroupsFrom(buttonObjects)
    /**
     *  Update `selected` state for changed groups
     */
    .forEach(updateButtonObjectsSelectedForGroup)

  /**
   *  Get the current `Configuration` from Render Data
   */
  const configuration = getConfiguration()

  /**
   *  Get the current `Configuration` according to computed `buttonObject` visibility
   */
  const CONFIGURATION = getConfigurationFromButtonObjects()

  /**
   *  Has the `Configuration` changed?
   */
  if (configuration !== CONFIGURATION) {
    /**
     *  Yes, it has
     *
     *  Set the changed `Configuration` to Render Data
     */
    setConfiguration(CONFIGURATION)

    /**
     *  Execute `updateAllButtonObjectsDisabled()` again
     */
    updateAllButtonObjectsDisabled()
  }
}

/**
 *  A reducer
 */
export function updateButtonObjectsDisabled (accumulator, buttonObject) {
  /**
   *  log('updateButtonObjectsDisabled')
   */

  /**
   *  Go to the DOM
   */
  const was = isDisabled(buttonObject)

  /**
   *  Get the `RegionConfiguration` and `Configuration`
   */
  const regionConfiguration = getRegionConfiguration()
  const configuration = getConfiguration()

  /**
   *  Use the `RegionConfiguration` and `Configuration`
   */
  const now = isDisabledForConfiguration(buttonObject, regionConfiguration + configuration)

  if (was !== now) { // `disabled` has changed
    /**
     *  Change the `UIElement` property because other computation depends on it
     */
    updateUIElementDisabled(buttonObject, now)

    return (
      accumulator.concat(buttonObject)
    )
  }

  return accumulator
}

/**
 *  Roll cages are black as standard (4th review ref 71). The matrix hides "Painted roll bar
 *  (body coloured)" (`+CCC`) while a cage is selected, and on UK cars "Painted roll bar/cage"
 *  (`+CCCPC`) shows only with a cage. When the selected finish is hidden, the `rolls` group
 *  falls back to its first enabled finish, "Roll bar – black finish" (`+RBCPGBSU`). That pick
 *  is automatic, but `updateAllButtonObjectsChanged` flags it as a user choice, so on its own
 *  it would stick: after going back to a roll bar, or after a UK painted cage goes back to a bar.
 *
 *  `updateRollFinishAfterChange` remembers the automatic pick (`AutoSelected`). Once body
 *  coloured shows, it re-selects it if it is the car's standard finish (in its default
 *  `Configuration`: the US Super Seven starts black) and the customer hasn't picked a finish
 *  since. Returns `true` when it restored body coloured.
 *
 *  "Back", "Forward" and a reload show a step of the browser history again, without the change
 *  that led to it. So `renderapp` keeps the automatic pick in the step
 *  (`isRollFinishAutoSelected`) and hands it back when the step shows again
 *  (`setRollFinishAutoSelected`).
 */
export const ROLLS_GROUP = 'rolls'
export const ROLL_FINISH_BLACK = '+RBCPGBSU'
export const ROLL_FINISH_BODY_COLOURED = '+CCC'

function getRollFinish (frontEndRule) {
  return (
    getAllButtonObjectsForGroup(ROLLS_GROUP)
      .find((buttonObject) => getButtonObjectFrontEndRule(buttonObject) === frontEndRule)
  )
}

export function getRollFinishSelected () {
  const buttonObject = (
    getAllButtonObjectsForGroup(ROLLS_GROUP)
      .filter(isEnabled)
      .find(isSelected)
  )

  return buttonObject ? getButtonObjectFrontEndRule(buttonObject) : ''
}

export function isRollFinishAutoSelected () {
  /**
   *  log('isRollFinishAutoSelected')
   */

  const black = getRollFinish(ROLL_FINISH_BLACK)

  return !!black && isSelected(black) && black.AutoSelected === true
}

export function setRollFinishAutoSelected (isAutoSelected = false) {
  /**
   *  log('setRollFinishAutoSelected')
   */

  const black = getRollFinish(ROLL_FINISH_BLACK)

  if (!black) return

  if (isAutoSelected && isSelected(black)) black.AutoSelected = true
  else delete black.AutoSelected
}

export function updateRollFinishAfterChange (buttonObject, rollFinishBefore = '') {
  /**
   *  log('updateRollFinishAfterChange')
   */

  const black = getRollFinish(ROLL_FINISH_BLACK)
  const bodyColoured = getRollFinish(ROLL_FINISH_BODY_COLOURED)

  if (!black || !bodyColoured) return false

  /**
   *  The customer picked a finish (theirs to keep), or black isn't selected
   */
  if (getButtonObjectGroup(buttonObject) === ROLLS_GROUP || !isSelected(black)) {
    delete black.AutoSelected
    return false
  }

  /**
   *  Black wasn't the finish before this change, so the configurator picked it
   */
  if (rollFinishBefore && rollFinishBefore !== ROLL_FINISH_BLACK) black.AutoSelected = true

  /**
   *  Body coloured is still hidden (a cage is selected)
   */
  if (isDisabled(bodyColoured) || !black.AutoSelected) return false

  delete black.AutoSelected

  const configuration = getConfiguration()

  if (!hasDefaultConfigurationFor(configuration)) return false

  const defaultConfiguration = getDefaultConfigurationFor(configuration)

  if (!defaultConfiguration.split('+').includes(ROLL_FINISH_BODY_COLOURED.slice(1))) return false

  if (hasButtonObjectUIElement(black)) getButtonObjectUIElement(black).checked = false

  updateUIElementSelected(bodyColoured)

  resetButtonObjectsChangedForGroup(ROLLS_GROUP)

  /**
   *  Derive the `Configuration` (and the roll colour picker's visibility) from the restored finish
   */
  updateAllButtonObjectsDisabled()

  return true
}

export function updateAllButtonObjectsPrices () {
  /**
   *  log('updateAllButtonObjectsPrices')
   */

  const {
    BasePrice: basePrice,
    OptionsPrice: optionsPrice
  } = renderAllButtonObjectsPrices()

  setBasePrice(basePrice)
  setOptionsPrice(optionsPrice)
}

export function updateButtonObjectsPrices (buttonObjects = getAllButtonObjects()) {
  /**
   *  log('updateButtonObjectsPrices')
   */

  const {
    BasePrice: basePrice,
    OptionsPrice: optionsPrice
  } = renderButtonObjectsPrices(buttonObjects)

  setBasePrice(basePrice)
  setOptionsPrice(optionsPrice)
}

export function renderAllButtonObjectsPrices () {
  /**
   *  log('renderAllButtonObjectsPrices')
   */

  return (
    getAllButtonObjects()
      .filter(isEnabled)
      .filter(isSelected)
      .reduce(reduceButtonObjectsPrices, {})
  )
}

export function renderButtonObjectsPrices (buttonObjects = getAllButtonObjects()) {
  /**
   *  log('renderButtonObjectsPrices')
   */

  return (
    buttonObjects
      .filter(isEnabled)
      .filter(isSelected)
      .reduce(reduceButtonObjectsPrices, {})
  )
}

/**
 *  US 620 flat-bundle invariant. The 620S (`+62SU`, $12,000) / 620R (`+62RU`,
 *  $13,000) upgrades are fixed-price packs: the flat fee covers the pack's OWN
 *  equipment, but options outside the pack (paint, decals, …) — and any pricier
 *  upgrade swapped in WITHIN a pack group — must still charge on top. The pack
 *  bundles exactly ONE standard item per group, so price suppression is per-ITEM
 *  (see `SPEC_STANDARD_620X` + `is620BundleSuppressed` below), never per-group.
 *
 *  These SETS are the groups whose selection the bundle changes vs the base pack
 *  (the cascade flips each from its free default to the spec item — directly, or
 *  indirectly, when the spec hides the base default). They
 *  drive the toggle-OFF revert in `renderapp.js`: re-pick each controlled group's
 *  $0 base default when the bundle is switched off. The bundle's own toggle group
 *  (`us_620X_spec`) is intentionally NOT listed, so it keeps charging the $12k/$13k.
 *  Derivation + regression check: .claude/_inspect_covered_groups.py.
 *  Inert for UK / non-620 configs, which never carry `+62SU`/`+62RU`.
 */
export const SPEC_GROUPS_620S = new Set([
  'differential', 'suspensions', 'antirollbar', 'wishbones', 'alloys', 'brakes',
  'mastercylinder', 'seatbelts', 'sequentialshiftlight', 'exhaust', 'bootcover',
  'sidepanels', 'gauges', 'dashboard', 'tunneltop',
  // `ontheroadpackage` (lightweight flywheel, item 49) is not listed: Round 4 ref 78
  // took the gearbox and flywheel options off every US car, so no US car has the group.
  // item 53: the base Seven S defaults `colourpack` to Silver ($0); the +62SU
  // cascade flips it to the Black components pack (+CPCPBKP, 620S standard), so the
  // toggle-OFF revert must re-pick the Silver base default. Price $0 via SPEC_STANDARD_620S.
  'colourpack'
])

export const SPEC_GROUPS_620R = new Set([
  'suspensions', 'wishbones', 'alloys', 'brakes', 'seats', 'steeringwheel',
  'rearwing', 'exhaust', 'gauges', 'frontwings', 'windscreen', 'bootcover',
  // `dashboard` is not listed: Carbon fibre is the US R standard with AND without
  // `+62RU` (V26 R-Pack), so the bundle does not change it — nothing to revert.
  // item 56: base US R now defaults the rear antirollbar to "With" ($0 standard). The
  // 620R bundle folds it into `+RCS` race suspension (no tile under `+62RU`, so it is
  // absent from SPEC_STANDARD_620R — nothing to suppress), but the toggle-OFF revert
  // must re-pick the base-R "With" default. Same role as 620S `antirollbar`.
  'antirollbar'
])

/**
 *  Per-ITEM price suppression. Under the bundle only the ONE standard item the
 *  pack includes per group rides free; every other option in that group (a pricier
 *  swap — e.g. trading the 620R-bundled carbon aeroscreen `+WARASNPCAW` for the
 *  full-weather kits `+CWD` $3,056 / `+CWBU` $4,211) charges its normal price on top.
 *
 *  Each map is {group -> standard FrontEndRule} = the row the `+62XU` cascade
 *  default-selects for that group on a fresh load. Derived from the matrix
 *  default-fire cascade (RulesMatrix col 11 DEF) under `+62SU`/`+62RU`, cross-checked
 *  vs the us_docs 620S/620R V26 PDF pack lists (they agree). Derivation + regression:
 *  scripts/matrix/_derive_standard_map.py. Keys mirror `SPEC_GROUPS_620X` minus
 *  `antirollbar` (both packs): no row is visible under the bundle, so nothing there is
 *  ever selected to charge — it folds into the suspension selection (`+SSP` for 620S,
 *  `+RCS` for 620R).
 */
export const SPEC_STANDARD_620S = new Map([
  ['differential', '+LSD'],
  ['suspensions', '+SSP'],
  ['wishbones', '+BAR'],
  ['alloys', '+ORCAOYPGRE'],
  ['brakes', '+BIG'],
  ['mastercylinder', '+MCP'],
  ['seatbelts', '+4PH'],
  ['sequentialshiftlight', '+SSL'],
  ['exhaust', '+ERE'],
  ['bootcover', '+BOOPITC'],
  ['sidepanels', '+INTPINN'],
  ['gauges', '+STG'],
  ['dashboard', '+DSHPDSC'],
  ['tunneltop', '+ITTPITC'],
  // item 53: Black components pack (+CPCPBKP) is the 620S standard. It costs $340 in
  // the S price column, so this suppression is what holds the 620S total at $62,195 —
  // without it the bundled Black pack would charge $340 on top.
  ['colourpack', '+CPCPBKP']
])

export const SPEC_STANDARD_620R = new Map([
  ['suspensions', '+RCS'],
  ['wishbones', '+BAR'],
  ['alloys', '+AP8AOYPBDA'],
  ['brakes', '+BIG'],
  ['seats', '+CCPC'],
  ['steeringwheel', '+MOMPMOLQRL'],
  ['rearwing', '+CRW'],
  ['exhaust', '+ERE'],
  ['gauges', '+STG'],
  ['frontwings', '+CFW'],
  ['windscreen', '+WARASNPCAW'],
  ['bootcover', '+BOOPITC']
])

export function is620BundleSuppressed (buttonObject) {
  const configuration = getConfiguration()

  let standardItems
  if (configuration.includes('+62SU')) standardItems = SPEC_STANDARD_620S
  else if (configuration.includes('+62RU')) standardItems = SPEC_STANDARD_620R
  else return false

  /**
   *  Suppress ONLY the pack's bundled standard item for its group. Every other
   *  option (non-pack groups → `.get()` is `undefined`; pricier swaps in a pack
   *  group → FrontEndRule differs) charges normally.
   */
  return standardItems.get(getButtonObjectGroup(buttonObject)) === getButtonObjectFrontEndRule(buttonObject)
}

/**
 *  Display price for a per-item label: the raw price, EXCEPT a 620-bundle
 *  standard item reads $0 — it rides free under the flat pack, so its label
 *  must not show the matrix price. Keeps the per-item labels in step with the
 *  total in `reduceButtonObjectsPrices`; the label paths (ui-element-container,
 *  options-summary) route through here instead of `getButtonObjectPrice`.
 */
export function getButtonObjectDisplayPrice (buttonObject, priceColumn = getPriceColumn()) {
  if (is620BundleSuppressed(buttonObject)) return 0

  return getButtonObjectPrice(buttonObject, priceColumn)
}

export function reduceButtonObjectsPrices ({ BasePrice = 0, OptionsPrice = 0 }, buttonObject) {
  /**
   *  log('renderButtonObjectsPrices')
   */

  if (hasButtonObjectBasePrice(buttonObject)) {
    /**
     *  During initialisation all `BasePrice` values are transformed to a number or to NaN
     */
    const basePrice = getButtonObjectBasePrice(buttonObject)

    /**
     *  During initialisation all `Prices` values are transformed to a number or to NaN
     */
    const price = getButtonObjectPrice(buttonObject, getPriceColumn())

    if (!isNaN(basePrice)) {
      BasePrice = isNaN(price) ? 0 : price
    } else {
      OptionsPrice += (isNaN(price) || is620BundleSuppressed(buttonObject)) ? 0 : price
    }
  }

  return {
    BasePrice,
    OptionsPrice
  }
}
